import { useState, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import { supabase } from "@/lib/supabase";
import usePageSEO from "@/hooks/usePageSEO";
import JsonLd from "@/components/feature/JsonLd";

const siteUrl = import.meta.env.VITE_SITE_URL || "https://ghbeauty.kr";

const breadcrumbSchema = {
  "@context": "https://schema.org",
  "@type": "BreadcrumbList",
  "itemListElement": [
    { "@type": "ListItem", "position": 1, "name": "Home", "item": siteUrl + "/" },
    { "@type": "ListItem", "position": 2, "name": "News", "item": siteUrl + "/news" }
  ]
};

const categories = ["All", "News", "Logistics", "Market Insights", "Company", "Brand Spotlight"];

const categoryLabels: Record<string, string> = {
  "market-insights": "Market Insights",
  "brand-spotlight": "Brand Spotlight",
};

const categoryIcons: Record<string, string> = {
  news: "ri-newspaper-line",
  logistics: "ri-truck-line",
  "market-insights": "ri-bar-chart-line",
  company: "ri-building-line",
  "brand-spotlight": "ri-star-line",
};

function formatDate(dateStr: string) {
  return new Date(dateStr).toLocaleDateString("en-US", {
    year: "numeric",
    month: "short",
    day: "numeric",
  });
}

interface Post {
  id: number;
  title: string;
  slug: string;
  excerpt: string | null;
  content: string | null;
  category: string | null;
  image_url: string | null;
  published_at: string | null;
}

export default function NewsPage() {
  usePageSEO({
    title: "News & Announcements | GH Corporation",
  });

  const [posts, setPosts] = useState<Post[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [activeCategory, setActiveCategory] = useState("All");

  const fetchPosts = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const { data, error: supaErr } = await supabase
        .from("posts")
        .select("*")
        .eq("is_published", true)
        .order("published_at", { ascending: false });

      if (supaErr) throw supaErr;
      setPosts((data as Post[]) || []);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to load news");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchPosts();
  }, [fetchPosts]);

  const filteredPosts = activeCategory === "All"
    ? posts
    : posts.filter((p) => p.category === activeCategory);

  return (
    <section className="w-full px-6 md:px-12 lg:px-20 pt-28 pb-24 md:pb-32 bg-background-100">
      <JsonLd data={breadcrumbSchema} />
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-end">
          <div className="lg:col-span-7">
            <div className="flex items-center gap-3 mb-6">
              <span className="w-8 h-px bg-accent-500" />
              <span className="font-label text-[11px] tracking-[0.22em] uppercase text-accent-700">
                Updates
              </span>
            </div>
            <h1 className="font-heading text-foreground-950 text-4xl md:text-5xl lg:text-6xl leading-[1.05] font-light">
              News & Announcements
            </h1>
          </div>
          <div className="lg:col-span-5 lg:pl-8">
            <p className="text-foreground-700 text-[15px] md:text-base leading-relaxed font-light">
              Stay informed about our latest brand additions, export updates, market insights, and company announcements.
            </p>
          </div>
        </div>

        {/* Category Filter */}
        <div className="mt-12 flex flex-wrap gap-2">
          {categories.map((cat) => (
            <button
              key={cat}
              onClick={() => setActiveCategory(cat)}
              className={`whitespace-nowrap cursor-pointer px-4 py-2 rounded-full text-[13px] font-label tracking-[0.06em] transition-all duration-300 ${
                activeCategory === cat
                  ? "bg-primary-500 text-background-50"
                  : "bg-background-50 text-foreground-700 border border-background-200/70 hover:border-accent-300 hover:text-accent-700"
              }`}
            >
              {cat}
            </button>
          ))}
        </div>

        {/* News Grid */}
        {loading && (
          <div className="mt-16 flex justify-center">
            <div className="w-10 h-10 border-2 border-accent-200 border-t-accent-500 rounded-full animate-spin" />
          </div>
        )}

        {error && (
          <div className="mt-16 text-center">
            <p className="text-foreground-600 text-[15px]">{error}</p>
            <button
              onClick={fetchPosts}
              className="mt-4 whitespace-nowrap cursor-pointer px-5 py-2.5 rounded-full bg-primary-500 text-background-50 hover:bg-primary-600 transition-colors font-label text-[13px] tracking-[0.12em] uppercase"
            >
              Retry
            </button>
          </div>
        )}

        {!loading && !error && filteredPosts.length === 0 && (
          <p className="mt-16 text-center text-foreground-500 text-[15px]">
            No posts found in this category.
          </p>
        )}

        {!loading && !error && filteredPosts.length > 0 && (
          <div className="mt-10 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6">
            {filteredPosts.map((post) => (
              <Link
                key={post.id}
                to={`/news/${post.slug}`}
                className="group flex flex-col rounded-2xl overflow-hidden bg-background-50 border border-background-200/70 hover:border-accent-300 transition-all duration-300 cursor-pointer"
              >
                {/* Image area */}
                <div className="relative h-52 md:h-56 overflow-hidden bg-gradient-to-br from-background-100 to-background-200">
                  {post.image_url ? (
                    <img
                      src={post.image_url}
                      alt={post.title}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                    />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-secondary-100 to-background-100">
                      <div className="w-14 h-14 flex items-center justify-center rounded-full bg-secondary-100 text-secondary-500 group-hover:bg-accent-100 group-hover:text-accent-500 transition-colors duration-300">
                        <i className={`${categoryIcons[post.category || "news"] || "ri-newspaper-line"} text-2xl`} />
                      </div>
                    </div>
                  )}
                  {/* Category tag */}
                  <div className="absolute top-4 left-4">
                    <span className="inline-block px-3 py-1 rounded-full bg-background-50/90 backdrop-blur text-[11px] font-label tracking-[0.08em] uppercase text-secondary-700 border border-background-200/70">
                      {categoryLabels[post.category || ""] || post.category || "News"}
                    </span>
                  </div>
                </div>

                {/* Content */}
                <div className="p-5 md:p-6 flex flex-col flex-1">
                  <p className="text-[12px] font-label tracking-[0.06em] text-foreground-400 uppercase">
                    {formatDate(post.published_at || "")}
                  </p>
                  <h3 className="mt-2 font-heading text-foreground-950 text-lg md:text-xl leading-tight group-hover:text-accent-700 transition-colors duration-300">
                    {post.title}
                  </h3>
                  {post.excerpt && (
                    <p className="mt-3 text-foreground-600 text-[14px] leading-relaxed line-clamp-3">
                      {post.excerpt}
                    </p>
                  )}
                  <div className="mt-auto pt-5 flex items-center gap-1.5 text-accent-700 text-[13px] font-label tracking-[0.04em]">
                    <span>Read more</span>
                    <i className="ri-arrow-right-line w-4 h-4 flex items-center justify-center" />
                  </div>
                </div>
              </Link>
            ))}
          </div>
        )}
      </div>
    </section>
  );
}